<template>
  <div
    class="pop_main"
    role="region"
    aria-label="메인 배너 팝업"
    v-if="showPopup"
    ref="popMainRef"
    tabindex="-1"
  >
    <div class="swiper__wrap">
      <swiper
        :pagination="mainPopupPagination"
        :modules="modules"
        :slides-per-view="1"
        :loop="true"
        :navigation="mainPopupNavigation"
        :autoplay="{ delay: 5000, disableOnInteraction: false }"
        @swiper="MainPopupSwiper"
        :aria-hidden="showPopup ? 'true' : 'false'"
        class="popup_swiper"
      >
        <swiper-slide :class="{ 'is-center': popupItems.length < 3 }">
          <span v-for="(item, idx) in popupItems" :key="idx">
            <a
              :href="formatUrl(item.url)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                :src="item.src"
                :alt="item.alt || `팝업 이미지 ${idx + 1}`"
              />
            </a>
          </span>
        </swiper-slide>
      </swiper>
      <!-- Custom Navigation -->
      <button
        class="main-popup__prev"
        @click="popupSwiperRef.value && popupSwiperRef.value.slidePrev()"
        aria-label="이전 슬라이드"
      >
        <i class="svg_icon icon_next white"></i>
      </button>
      <button
        class="main-popup__next"
        @click="popupSwiperRef.value && popupSwiperRef.value.slideNext()"
        aria-label="다음 슬라이드"
      >
        <i class="svg_icon icon_next white"></i>
      </button>
    </div>

    <div class="main-popup__controls">
      <div class="main-popup__bullets" aria-label="슬라이드 위치 표시"></div>
      <button
        v-if="popupSlideCount > 1"
        class="main-popup__play"
        type="button"
        :aria-label="popupIsPlaying ? '자동재생 정지' : '자동재생 시작'"
        :aria-pressed="popupIsPlaying ? 'true' : 'false'"
        @click="toggleMainPopupAutoplay"
      >
        <span v-if="popupIsPlaying" aria-hidden="true">
          <img src="@/assets/img/icon_stop.svg" alt="" />
        </span>
        <span v-else aria-hidden="true">▶</span>
        <span class="sr-only">{{ popupIsPlaying ? "정지" : "재생" }}</span>
      </button>
    </div>
    <div class="main-popup__btns" v-if="popupItems.length > 0">
      <button
        type="button"
        class="btn btn--white btn--line btn--m"
        v-on:click="hideForAWeek"
      >
        일주일 열지 않기
      </button>
      <button
        type="button"
        class="btn btn--white btn--line btn--m"
        @click="closePopup"
      >
        모두 닫기<i class="svg_icon icon_del white"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { sha256HashString } from "@/component/Crypto.js";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { nextTick, onMounted, ref, watch } from "vue";

export default {
  props: {
    popupItems: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    Swiper,
    SwiperSlide,
  },
  setup(props, { emit }) {
    // data 정의
    // Swiper 모듈
    const modules = [Autoplay, Pagination, Navigation];

    // 각 popup 전용 상태
    const mainPopupPagination = {
      el: ".main-popup__bullets",
      clickable: true,
      bulletClass: "bullet",
      bulletActiveClass: "active",
      renderBullet: (i, cls) =>
        `<button type="button" class="${cls}" aria-label="${
          i + 1
        }번 슬라이드로 이동">
      <span class="sr-only">${i + 1}번</span>
    </button>`,
    };

    const mainPopupNavigation = {
      prevEl: ".main-popup__prev",
      nextEl: ".main-popup__next",
    };

    const popupSwiperRef = ref(null);
    const popupIsPlaying = ref(true);
    const popupSlideCount = ref(4); // 실제 슬라이드 수
    const showPopup = ref(true);
    const popMainRef = ref(null);

    // methods 정의
    const closePopup = () => {
      showPopup.value = false;
      emit("close");
    };
    const formatUrl = (url) => {
      // 배너 클릭시 url이동
      if (!url) return "#";
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
      }
      return `http://${url}`;
    };
    // Swiper init 시 호출
    const MainPopupSwiper = (swiper) => {
      popupSwiperRef.value = swiper;
      popupIsPlaying.value = !!(
        swiper.params.autoplay && swiper.autoplay?.running
      );

      swiper.on("autoplayStart", () => (popupIsPlaying.value = true));
      swiper.on("autoplayStop", () => (popupIsPlaying.value = false));
    };

    // 재생/정지 토글
    const toggleMainPopupAutoplay = () => {
      const s = popupSwiperRef.value;
      if (!s || !s.autoplay) return;
      s.autoplay.running ? s.autoplay.stop() : s.autoplay.start();
    };

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    const setCookie = (name, value, days) => {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
    };

    const hideForAWeek = async () => {
      const currentHash = await sha256HashString(
        JSON.stringify(props.popupItems)
      );
      const expireDate = new Date();
      expireDate.setDate(expireDate.getDate() + 7);
      const data = {
        hash: currentHash,
        expire: expireDate.toISOString(),
      };

      setCookie("hidePopups", encodeURIComponent(JSON.stringify(data)), 7);
      showPopup.value = false;
    };

    // watch 정의
    watch(
      () => props.popupItems,
      async (newVal) => {
        showPopup.value = newVal.length > 0;
        if (newVal.length === 0) {
          return;
        }
        const hash = await sha256HashString(JSON.stringify(newVal));
        const cookieData = getCookie("hidePopups");

        if (cookieData) {
          const saved = JSON.parse(decodeURIComponent(cookieData));
          const now = new Date();
          if (saved.hash === hash && new Date(saved.expire) > now) {
            showPopup.value = false;
          }
        }
      },
      { immediate: true, deep: true }
    );

    // life cycle 정의
    onMounted(() => {
      if (showPopup.value) {
        nextTick(() => {
          popMainRef.value?.focus();
        });
      }
    });

    // html에서 사용될 데이터 정의
    return {
      popupSlideCount,
      toggleMainPopupAutoplay,
      MainPopupSwiper,
      popupSwiperRef,
      popupIsPlaying,
      mainPopupPagination,
      mainPopupNavigation,
      modules,
      hideForAWeek,
      showPopup,
      closePopup,
      popMainRef,
      formatUrl,
    };
  },
};
</script>
